import { useEffect, useRef, type KeyboardEvent } from 'react';
import {
  BOARD_SIZE,
  UNIT_SIZE,
  composeBoard,
  maskValues,
  rowOf,
  columnOf,
  type CellValue,
} from '../../game/board';
import type { GameSession, SessionAction } from '../../game/session';
import { translate } from '../../i18n/translations';
import type { Language } from '../../platform/settings/settings';
import styles from './GameBoard.module.css';

type GameBoardProps = {
  language: Language;
  session: GameSession;
  candidateMode: boolean;
  onAction: (action: SessionAction) => void;
};

const LAST_UNIT_INDEX = UNIT_SIZE - 1;
const PREVIOUS_ROW_OFFSET = BOARD_SIZE - UNIT_SIZE;

function cellLabel(
  language: Language,
  session: GameSession,
  index: number,
  value: CellValue,
): string {
  const parts = [
    `${translate(language, 'row')} ${String(rowOf(index) + 1)}, ${translate(language, 'column')} ${String(
      columnOf(index) + 1,
    )}`,
    session.puzzle[index] === 0
      ? translate(language, 'editableCell')
      : translate(language, 'fixedCell'),
  ];
  if (value === 0) {
    const candidates = maskValues(session.candidates[index] ?? 0);
    parts.push(
      candidates.length === 0
        ? translate(language, 'emptyCell')
        : `${translate(language, 'cellCandidates')} ${candidates.map(String).join(', ')}`,
    );
  } else {
    parts.push(`${translate(language, 'cellValue')} ${String(value)}`);
  }
  if (session.conflicts.has(index)) {
    parts.push(translate(language, 'conflictCell'));
  }
  if (session.incorrectCells.has(index)) {
    parts.push(translate(language, 'incorrectCell'));
  }
  if (session.selectedIndex === index) {
    parts.push(translate(language, 'selected'));
  }
  return parts.join(', ');
}

export function GameBoard({ language, session, candidateMode, onAction }: GameBoardProps) {
  const selectedRef = useRef<HTMLButtonElement>(null);
  const board = composeBoard(session.puzzle, session.entries);

  useEffect(() => {
    if (document.activeElement?.getAttribute('role') === 'gridcell') {
      selectedRef.current?.focus();
    }
  }, [session.selectedIndex]);

  if (session.status === 'paused') {
    return <div className={styles.paused}>{translate(language, 'pausedBoard')}</div>;
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    let nextIndex: number | null = null;
    switch (event.key) {
      case 'ArrowRight':
        nextIndex = rowOf(index) * UNIT_SIZE + ((columnOf(index) + 1) % UNIT_SIZE);
        break;
      case 'ArrowLeft':
        nextIndex = rowOf(index) * UNIT_SIZE + ((columnOf(index) + LAST_UNIT_INDEX) % UNIT_SIZE);
        break;
      case 'ArrowDown':
        nextIndex = (index + UNIT_SIZE) % BOARD_SIZE;
        break;
      case 'ArrowUp':
        nextIndex = (index + PREVIOUS_ROW_OFFSET) % BOARD_SIZE;
        break;
      case 'Backspace':
      case 'Delete':
        event.preventDefault();
        onAction({ type: 'clear' });
        return;
      default: {
        const value = Number(event.key);
        if (Number.isInteger(value) && value >= 1 && value <= 9) {
          event.preventDefault();
          const cellValue = value as CellValue;
          onAction(
            candidateMode
              ? { type: 'toggleCandidate', value: cellValue }
              : { type: 'enterValue', value: cellValue },
          );
        }
        return;
      }
    }
    event.preventDefault();
    onAction({ type: 'select', index: nextIndex });
  };

  return (
    <div className={styles.grid} role="grid" aria-label={translate(language, 'boardLabel')}>
      {Array.from({ length: UNIT_SIZE }, (_, row) => (
        <div className={styles.row} role="row" key={row}>
          {Array.from({ length: UNIT_SIZE }, (_, column) => {
            const index = row * UNIT_SIZE + column;
            const value = board[index] ?? 0;
            const fixed = session.puzzle[index] !== 0;
            const selected = session.selectedIndex === index;
            const conflict = session.conflicts.has(index);
            const incorrect = session.incorrectCells.has(index);
            const candidates = maskValues(session.candidates[index] ?? 0);
            return (
              <button
                key={index}
                className={styles.cell}
                data-box-column={column % 3 === 2 && column !== LAST_UNIT_INDEX ? 'end' : undefined}
                data-box-row={row % 3 === 2 && row !== LAST_UNIT_INDEX ? 'end' : undefined}
                data-conflict={conflict || undefined}
                data-fixed={fixed || undefined}
                data-incorrect={incorrect || undefined}
                data-selected={selected || undefined}
                type="button"
                role="gridcell"
                aria-label={cellLabel(language, session, index, value)}
                aria-readonly={fixed}
                aria-selected={selected}
                tabIndex={selected ? 0 : -1}
                ref={selected ? selectedRef : undefined}
                onClick={() => onAction({ type: 'select', index })}
                onKeyDown={(event) => handleKeyDown(event, index)}
              >
                {value === 0 ? (
                  <span className={styles.candidates} aria-hidden="true">
                    {Array.from({ length: UNIT_SIZE }, (_, candidateIndex) => {
                      const candidate = (candidateIndex + 1) as CellValue;
                      return (
                        <span key={candidate}>
                          {candidates.includes(candidate) ? candidate : ''}
                        </span>
                      );
                    })}
                  </span>
                ) : (
                  <span aria-hidden="true">{value}</span>
                )}
                {conflict || incorrect ? (
                  <span className={styles.conflictMark} aria-hidden="true">
                    !
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
